import type { RecipeVariants } from '@vanilla-extract/recipes'
import classNames from 'classnames'
import z from 'zod'
import type { AreEquivalent } from '../../types.ts'
import { useHasScrollbar } from '../../utils/hooks/use-has-scrollbar'
import * as styles from './floating-footer.css.ts'

// Match the footer to its drawer's width: `wide` is the viewport-pegged 920 finding drawer; `standard`
// is the fixed 920 used by the integration setup/manage drawers.
export const FloatingFooterWidthSchema = z.enum(['wide', 'standard'])
export type FloatingFooterWidth = z.infer<typeof FloatingFooterWidthSchema>

// Ties the enum to the recipe so a variant added to one is a build error until it reaches the other.
type RecipeFloatingFooterWidth = NonNullable<NonNullable<RecipeVariants<typeof styles.floatingFooter>>['width']>
true satisfies AreEquivalent<FloatingFooterWidth, RecipeFloatingFooterWidth>

export type FloatingFooterProps = React.PropsWithChildren<{
  drawerContentRef: React.RefObject<HTMLDivElement | null>
  className?: string
  width?: FloatingFooterWidth
}>

/**
 * `width` matches the footer to its drawer: `wide` scales with the viewport, `standard` is fixed.
 *
 * This component should wrap your action buttons. It should be placed inside the Dialog.Portal, but outside the Dialog.Content.
 * Should be used in conjunction with:
 *   * A ref to the Dialog.Content.
 *   * @see styles.drawerContentPaddingAdjustment on the associated Dialog.Content
 *   * The `data-has-floating-footer` attribute on the associated Dialog.Content (should be `true | undefined`, not `boolean | undefined`)
 *
 * @example
 * import * as styles from '...'
 * import * as footerStyles from './floating-footer.css'
 *
 * const hasFloatingFooter = true
 *
 * <Dialog.Portal>
 *   <Dialog.Content
 *     ref={drawerContentRef}
 *     className={classNames(styles.myDialogContentStyle, footerStyles.drawerContentPaddingAdjustment)}
 *     data-has-floating-footer={hasFloatingFooter ? true : undefined}
 *     onInteractOutside={event => event.preventDefault()}
 *   >
 *     { ... }
 *   </Dialog.Content>
 *   <FloatingFooter drawerContentRef={drawerContentRef} width="wide">
 *   </FloatingFooter
 * </Dialog.Portal>
 */
export const FloatingFooter: React.FC<FloatingFooterProps> = ({
  children,
  drawerContentRef,
  className,
  width = 'wide',
}) => {
  const hasScrollbar = useHasScrollbar(drawerContentRef)

  return (
    <div className={classNames(styles.floatingFooter({ width }), className)} data-has-scrollbar={hasScrollbar}>
      {children}
    </div>
  )
}
